// Offline file store — photos & signatures captured with no reception are
// kept in IndexedDB as blobs, then uploaded automatically when back online.
// Queued writes reference them as "offline://<id>" until the real
// storage_path exists.

const DB_NAME = "fl_offline";
const STORE = "files";

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx(db, mode, fn) {
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const out = fn(store);
    t.oncomplete = () => resolve(out?.result !== undefined ? out.result : out);
    t.onerror = () => reject(t.error);
  });
}

export async function saveOfflineFile(file, category = "general") {
  const db = await openDb();
  const rec = {
    id: `of-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    blob: file,
    name: file.name || `upload-${Date.now()}.bin`,
    type: file.type || "application/octet-stream",
    size: file.size || 0,
    category,
    created_at: new Date().toISOString(),
  };
  await tx(db, "readwrite", (s) => s.put(rec));
  notifyFilesChange();
  return rec;
}

export async function listOfflineFiles() {
  try {
    const db = await openDb();
    const all = await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, "readonly");
      const req = t.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    return all.map(({ blob, ...meta }) => meta);
  } catch {
    return [];
  }
}

export async function countOfflineFiles() {
  return (await listOfflineFiles()).length;
}

export async function deleteOfflineFile(id) {
  const db = await openDb();
  await tx(db, "readwrite", (s) => s.delete(id));
  notifyFilesChange();
}

function notifyFilesChange() {
  try { window.dispatchEvent(new CustomEvent("fl-offline-files-change")); } catch { /* SSR */ }
}

// Upload every stored file. Returns { <offlineId>: <real storage_path> }.
// Throws on network failure (still offline) so callers can stop the sync.
export async function uploadOfflineFiles(api) {
  const db = await openDb();
  const all = await new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  const map = {};
  const dropped = [];
  for (const f of all) {
    const fd = new FormData();
    fd.append("file", new File([f.blob], f.name, { type: f.type }));
    fd.append("category", f.category || "general");
    try {
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        __flReplay: true,
      });
      map[f.id] = data.storage_path;
      await tx(db, "readwrite", (s) => s.delete(f.id));
    } catch (e) {
      if (!e?.response) throw e; // network gone again — abort sync
      // Server rejected the file (too big etc.) — drop it so it can't block forever
      dropped.push(f.id);
      await tx(db, "readwrite", (s) => s.delete(f.id));
    }
  }
  notifyFilesChange();
  return { map, dropped };
}
